/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import {
    BufferGeometry,
    Color,
    Float32BufferAttribute,
    Mesh,
    Object3D,
    PerspectiveCamera,
    Scene,
    ShaderMaterial,
    Vector2,
} from 'three';

import type Renderer from '../../../globals/Renderer';

import PerformanceProfile from '../../../utils/PerformanceProfile';
import SoundController from '../../../utils/SoundController';

import { fragmentShader, vertexShader } from './shaders';
import { soundTimeline } from './soundTimeline';

const GLOBAL_CONFIG = {
    speed: 0.8260000000000001,
    frequency: 7.609000000000001,
    persistence: 0.28300000000000003,
    gradient: 4.303999999999999,
    noiseScaleX: 0.707,
    noiseScaleY: 0.5980000000000001,
    borderAlpha: 0.32,
    borderSize: 2,
    alpha: 0.783,
};

const CONFIG = [
    {
        colorA: '#eb8500',
        colorB: '#ff4800',
    },
    {
        colorA: '#eb8500',
        colorB: '#ff4800',
    },
    {
        colorA: '#eb8500',
        colorB: '#ff4800',
    },
    {
        // colorA: '#eb8500',
        // colorB: '#ff4800',

        // colorA: '#ff3c0b',
        // colorB: '#ff9a00'

        colorA: '#ff3300',
        colorB: '#c6a44c',
    },
];

let GUI;

const addGUI = async () => {
    await PerformanceProfile.devModeReady();

    GUI = PerformanceProfile.GUI.addFolder({
        title: `Floor Game Fire`,
        expanded: false,
    });

    GUI.addButton({ title: 'Export Config' }).on('click', () => {
        PerformanceProfile.exportConfig('FireCard', { ...GLOBAL_CONFIG, ...CONFIG });
    });

    GUI.addInput(GLOBAL_CONFIG, 'speed', { min: 0, max: 2.0, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'frequency', { min: 0, max: 10.0, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'persistence', { min: 0, max: 2.0, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'gradient', { min: 0, max: 12.0, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'noiseScaleX', { min: 0, max: 5.0, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'noiseScaleY', { min: 0, max: 5.0, step: 0.001 });

    GUI.addInput(GLOBAL_CONFIG, 'alpha', { min: 0, max: 1.0, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'borderAlpha', { min: 0, max: 1.0, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'borderSize', { min: 0, max: 10, step: 0.001 });

    for (let i = 0; i < CONFIG.length; i++) {
        if (i === 3) {
            const folder = GUI.addFolder({
                // title: `Tier ${i + 1}`,
                title: `Colors`,
                expanded: false,
            });

            folder.addInput(CONFIG[i], 'colorA');
            folder.addInput(CONFIG[i], 'colorB');
        }
    }
};

if (PerformanceProfile.devMode) addGUI();

// let tier = 0;

interface FireOptions {
    hasBorder?: boolean;
}

export default class Fire extends Object3D {
    camera: PerspectiveCamera;
    scene: Scene;
    // TODO: Re-write the logic so that `mesh` is assigned
    // within the constructor and not possibly `undefined`.
    mesh: Mesh;
    // TODO: Re-write the logic so that `soundController` is assigned
    // within the constructor and not possibly `undefined`.
    soundController: SoundController;

    width = 1;
    height = 1;
    tier = 1;

    time: number;
    hasBorder: boolean;

    // TODO: Why do we accept `renderer` but not use it?
    constructor(renderer: Renderer, options?: FireOptions) {
        super();

        this.camera = new PerspectiveCamera(30, 1 / 1, 0.01, 1000);

        this.scene = new Scene();
        this.scene.add(this);

        // Tiering implemented in case we need to add new colors.
        // TODO: Why does `tier` default to `3`?
        this.tier = 3;

        // TODO: What is the logic behind the `time` assignment?
        this.time = Math.random() * 1000;

        this.width = 1;
        this.height = 1;

        this.hasBorder = options?.hasBorder ?? false;

        this.initMesh();
        this.initSound();

        if (PerformanceProfile.devMode) {
            GUI.on('change', this.updateUniforms.bind(this));
        }
    }

    initMesh() {
        const geometry = new BufferGeometry();

        geometry.setAttribute('position', new Float32BufferAttribute([-1, 3, 0, -1, -1, 0, 3, -1, 0], 3));
        geometry.setAttribute('uv', new Float32BufferAttribute([0, 2, 0, 0, 2, 0], 2));

        const material = new ShaderMaterial({
            uniforms: {
                uColorA: { value: new Color(CONFIG[this.tier].colorA) },
                uColorB: { value: new Color(CONFIG[this.tier].colorB) },
                uTime: { value: 0.0 },

                uFrequency: { value: GLOBAL_CONFIG.frequency },
                uPersistence: { value: GLOBAL_CONFIG.persistence },
                uGradient: { value: GLOBAL_CONFIG.gradient },

                uNoiseScaleX: { value: GLOBAL_CONFIG.noiseScaleX },
                uNoiseScaleY: { value: GLOBAL_CONFIG.noiseScaleY },

                uResolution: { value: new Vector2(1, 1) },
                uBorderRadius: { value: 24 },

                uAlpha: { value: GLOBAL_CONFIG.alpha },
                uBorderAlpha: { value: GLOBAL_CONFIG.borderAlpha },
                uBorderSize: { value: GLOBAL_CONFIG.borderSize },
            },
            vertexShader,
            fragmentShader,
            defines: {
                USE_BORDER: this.hasBorder,
            },
        });

        this.mesh = new Mesh(geometry, material);
        this.add(this.mesh);
    }

    initSound() {
        this.soundController = new SoundController(soundTimeline, '/assets/webgl/audio/on-fire/');
    }

    setSize(width: number, height: number) {
        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();

        this.mesh.material.uniforms.uResolution.value.x = width;
        this.mesh.material.uniforms.uResolution.value.y = height;

        if (this.layer) {
            this.mesh.material.uniforms.uBorderRadius.value = this.layer.borderRadius * this.layer.pixelRatio;
            this.mesh.material.uniforms.uBorderSize.value = GLOBAL_CONFIG.borderSize * this.layer.pixelRatio;
        }
    }

    updateUniforms() {
        this.mesh.material.uniforms.uColorA.value.setStyle(CONFIG[this.tier].colorA);
        this.mesh.material.uniforms.uColorB.value.setStyle(CONFIG[this.tier].colorB);

        this.mesh.material.uniforms.uFrequency.value = GLOBAL_CONFIG.frequency;
        this.mesh.material.uniforms.uPersistence.value = GLOBAL_CONFIG.persistence;

        this.mesh.material.uniforms.uGradient.value = GLOBAL_CONFIG.gradient;

        this.mesh.material.uniforms.uNoiseScaleX.value = GLOBAL_CONFIG.noiseScaleX;
        this.mesh.material.uniforms.uNoiseScaleY.value = GLOBAL_CONFIG.noiseScaleY;

        this.mesh.material.uniforms.uAlpha.value = GLOBAL_CONFIG.alpha;
        this.mesh.material.uniforms.uBorderAlpha.value = GLOBAL_CONFIG.borderAlpha;
        this.mesh.material.uniforms.uBorderSize.value = GLOBAL_CONFIG.borderSize;
    }

    update(elapsed: number) {
        this.mesh.material.uniforms.uTime.value = this.time + elapsed * GLOBAL_CONFIG.speed;
    }

    animateIn() {
        this.soundController.playTimeline();
    }

    delete() {
        this.soundController.killTimeline();
    }
}
